import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useApiClient } from "../../../api";
import { getSchoolsForUser } from "../../auth/Utils";
import { useAuthenticationStore } from "../../../stores/auth";

export const CreateClassDialog = ({ open, setOpen, onCreate }) => {
  const ApiClient = useApiClient();
  const [name, setName] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [yearGroups, setYearGroups] = useState([]);
  const [yearGroup, setYearGroup] = useState("");
  const [school, setSchool] = useState("");
  const [subject, setSubject] = useState("");
  const [error, setError] = useState("");
  const { updateUserInfo } = useAuthenticationStore();

  const schools = getSchoolsForUser();

  const fetchSubjectsAndLevels = async () => {
    try {
      const response = await ApiClient.core.getSubjectsAndLevels();
      if (response) {
        setSubjects(response.subjects);
        setEducationLevels(response.educationLevels);
      }
    } catch (error) {
      console.error("Failed to fetch subjects and levels", error);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await ApiClient.schools.getAcademicYears();
      if (response) {
        const years = response.academicYears;
        setAcademicYears(years);
        setAcademicYear(years.find((year) => year.isCurrent) || "");
      }
    } catch (error) {
      console.error("Failed to fetch academic years", error);
    }
  };

  useEffect(() => {
    fetchSubjectsAndLevels();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (schools?.length === 1) {
      setSchool(schools[0]);
    }
  }, [schools]);

  useEffect(() => {
    // NOTE: This seems backwards, but confirmed education level from a question (during assignment workflow)
    // does not include year groups (at the moment)
    if (!educationLevel) {
      setYearGroups([]);
    } else {
      const matchingEducationLevel = educationLevels.find(
        (level) => level.id === educationLevel.id,
      );
      if (matchingEducationLevel) {
        setYearGroups(matchingEducationLevel.yearGroups || []);
      } else {
        setYearGroups([]);
      }
    }
  }, [educationLevel, educationLevels]);

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setName("");
    setAcademicYear(academicYears.find((year) => year.isCurrent) || "");
    setYearGroup("");
    setSubject("");
    setEducationLevel("");
  };

  const handleSubmit = async () => {
    setError("");
    try {
      const response = await ApiClient.schools.createSchoolClass({
        name,
        academicYear: academicYear.id,
        yearGroup: yearGroup.id,
        subject: subject.id,
        school: school.id,
      });
      if (response.success) {
        const userData = await ApiClient.users.me();
        updateUserInfo(userData);
        onCreate(response.schoolClass);
        handleClose();
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error("Failed to create a class", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant={"h4"}>Add a new Class</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity={"error"}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            placeholder="Class Name"
            label={"Class Name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <FormControl>
            <InputLabel id="academic-year-label">Academic Year</InputLabel>
            <Select
              labelId="academic-year-label"
              label="Academic Year"
              onChange={(e) => {
                setAcademicYear(e.target.value);
              }}
              value={academicYear}
              fullWidth
            >
              {academicYears.map((year) => (
                <MenuItem key={year.id} value={year}>
                  {year.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {schools?.length > 1 && (
            <FormControl>
              <InputLabel id="schools-label">School</InputLabel>
              <Select
                labelId={"schools-label"}
                label={"School"}
                value={school}
                onChange={(e) => {
                  setSchool(e.target.value);
                }}
                fullWidth
              >
                {schools?.map((school) => (
                  <MenuItem key={school.id} value={school}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl>
            <InputLabel id="education-level-label">Education Level</InputLabel>
            <Select
              labelId={"education-level-label"}
              label={"Education Level"}
              value={educationLevel}
              onChange={(e) => {
                setEducationLevel(e.target.value);
                setYearGroup("");
              }}
              fullWidth
            >
              {educationLevels.map((level) => (
                <MenuItem key={level.id} value={level}>
                  {level.level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel id="year-group-label">Year Group</InputLabel>
            <Select
              labelId={"year-group-label"}
              label={"Year Group"}
              value={yearGroup}
              onChange={(e) => setYearGroup(e.target.value)}
              fullWidth
              disabled={!educationLevel}
            >
              {yearGroups.map((year) => (
                <MenuItem key={year.id} value={year}>
                  Year {year.yearGroup}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel id="subject-label">Subject</InputLabel>
            <Select
              labelId={"subject-label"}
              label={"Subject"}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
            >
              {subjects.map((sub) => (
                <MenuItem key={sub.id} value={sub}>
                  {sub.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant={"contained"}
          disabled={!name || !educationLevel || !yearGroup || !subject}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
